import { Button, UseStepContext } from '@consigli/facade';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiGlobeAlt, HiOutlineXMark } from 'react-icons/hi2';

import { useWizardContext } from '../wizard-context';

interface UrlsStepProps {
  context: UseStepContext;
}

const ARCGIS_LAYERS = [
  { key: 'layer-electric-transmission-lines', category: 'active' },
  { key: 'layer-railroads', category: 'active' },
  { key: 'layer-private-schools', category: 'active' },
  { key: 'layer-public-universities', category: 'active' },
  { key: 'layer-gas-pipelines', category: 'active' },
  { key: 'layer-broadband-coverage', category: 'active' },
  { key: 'layer-wetlands', category: 'active' },
  { key: 'layer-rivers-streams', category: 'active' },
  { key: 'layer-public-schools', category: 'active' },
  { key: 'layer-power-plants', category: 'active' },
  { key: 'layer-aviation-facilities', category: 'active' },
  { key: 'layer-structures', category: 'active' },
  { key: 'layer-natura-2000', category: 'eu' },
  { key: 'layer-water-framework', category: 'eu' },
  { key: 'layer-power-infrastructure', category: 'eu' },
];

export const UrlsStep: FC<UrlsStepProps> = ({ context }) => {
  const { t } = useTranslation();
  const { urls, setUrls } = useWizardContext();
  const { next, previous, hasNext, hasPrevious } = context();
  const [urlInput, setUrlInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');

  const handleAddUrl = useCallback(() => {
    const trimmedUrl = urlInput.trim();
    const trimmedDescription = descriptionInput.trim();
    if (trimmedUrl && !urls.some((item) => item.url === trimmedUrl)) {
      setUrls([...urls, { url: trimmedUrl, description: trimmedDescription }]);
      setUrlInput('');
      setDescriptionInput('');
    }
  }, [urlInput, descriptionInput, urls, setUrls]);

  const handleRemoveUrl = useCallback(
    (urlToRemove: string) => {
      setUrls(urls.filter((item) => item.url !== urlToRemove));
    },
    [urls, setUrls],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddUrl();
      }
    },
    [handleAddUrl],
  );

  return (
    <div className="w-full">
      <div className="bg-day-light-3 border border-day-light-1 rounded-lg p-4 w-full mt-3 mb-6">
        <div className="flex items-start gap-3 mb-3">
          <HiGlobeAlt className="w-5 h-5 text-brand-default flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-day-neutral-dark">
              {t('package-creation.available-arcgis-layers')}
            </p>
            <p className="text-sm text-day-neutral-subtle mt-1">
              {t('package-creation.arcgis-layers-description')}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-day-neutral-subtle uppercase tracking-wider mb-2">
              {t('package-creation.active-arcgis-layers')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
              {ARCGIS_LAYERS.filter((layer) => layer.category === 'active').map((layer) => (
                <div
                  key={layer.key}
                  className="flex items-center gap-2 text-sm text-day-neutral-dark pl-3"
                >
                  <span className="text-brand-default">•</span>
                  <span>{t(`package-creation.${layer.key}`)}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-day-neutral-subtle uppercase tracking-wider mb-2">
              {t('package-creation.eu-arcgis-layers')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
              {ARCGIS_LAYERS.filter((layer) => layer.category === 'eu').map((layer) => (
                <div
                  key={layer.key}
                  className="flex items-center gap-2 text-sm text-day-neutral-dark pl-3"
                >
                  <span className="text-brand-default">•</span>
                  <span>{t(`package-creation.${layer.key}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 mb-4 text-base font-semibold text-day-neutral-subtle">
        {t('package-creation.urls-label')}{' '}
        <span className="text-sm font-normal text-day-neutral-subtle">
          {t('package-creation.urls-optional')}
        </span>
      </p>
      <div className="p-4 border border-day-light-2 rounded bg-day-light-4 space-y-3">
        <input
          type="url"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('package-creation.url-placeholder')}
          className="w-full px-3 py-2 border border-day-light-2 rounded focus:outline-none focus:border-day-dark-1 bg-white text-sm placeholder:text-sm"
        />
        <textarea
          value={descriptionInput}
          onChange={(e) => setDescriptionInput(e.target.value)}
          placeholder={t('package-creation.description-placeholder')}
          rows={3}
          className="w-full px-3 py-2 border border-day-light-2 rounded focus:outline-none focus:border-day-dark-1 resize-vertical bg-white text-sm placeholder:text-sm"
        />
        <p className="text-xs text-day-neutral-subtle">
          {t('package-creation.layer-description-required')}
        </p>
        <div className="flex justify-end">
          <Button
            type="button"
            primary
            rounded
            onClick={handleAddUrl}
            disabled={!urlInput.trim() || !descriptionInput.trim()}
          >
            {t('package-creation.add-button')}
          </Button>
        </div>
      </div>

      {urls.length > 0 && (
        <ul className="mt-4 space-y-3">
          {urls.map((item) => (
            <li key={item.url} className="p-3 bg-day-light-3 rounded">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-day-neutral-dark hover:underline truncate block"
                  >
                    {item.url}
                  </a>
                  {item.description && (
                    <p className="text-xs text-day-neutral-subtle mt-1">{item.description}</p>
                  )}
                </div>
                <Button
                  tertiary
                  icon={HiOutlineXMark}
                  onClick={() => handleRemoveUrl(item.url)}
                  className="p-1 flex-shrink-0"
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-stretch gap-2 mt-6">
        {hasPrevious && (
          <Button secondary rounded onClick={previous} type="button">
            {t('package-creation.back')}
          </Button>
        )}
        {hasNext && (
          <Button primary rounded onClick={next} type="button">
            {t('package-creation.next')}
          </Button>
        )}
      </div>
    </div>
  );
};
